import * as Y from 'yjs';
import { convertValue, defaultFieldConfig, FIELD_TYPES, optionsFromValues, valueToText } from './fields.ts';
import { now, uid } from './ids.ts';
import {
  BOARD_SHAPES,
  isColorName,
  isProjectSection,
  isVotable,
  PROJECT_SECTIONS,
  type BoardData,
  type BoardItem,
  type BoardShape,
  type CollectionData,
  type CollectionGroup,
  type CollectionTreeNode,
  type ColorName,
  type FieldDef,
  type FileMeta,
  type Person,
  type PollData,
  type PollOption,
  type PollVote,
  type ProjectMeta,
  type ProjectSection,
  type RecordData,
  type ViewDef,
} from './model.ts';
import { compareOrder, orderAfterLast, orderAtIndex, orderBeforeFirst, orderBetween, ordersBetween, sortByOrder, type Ordered } from './order.ts';
import { readRichText, writeRich, type BodyText, type RichNode } from './rich.ts';
import { singularize } from './words.ts';

/**
 * Y.Doc layout of one project (one document = one project = one sync room):
 *
 *   meta         Y.Map   id, name, description, icon, color, createdAt, schemaVersion, template, sections
 *   collections  Y.Map<id, Y.Map>   scalar keys + `fields` Y.Map<fieldId, FieldDef> + `views` Y.Map<viewId, ViewDef>
 *   groups       Y.Map<id, CollectionGroup>   the sidebar's folders (id, name, order)
 *   records      Y.Map<id, Y.Map>   scalar keys (incl. `cover`) + `values` Y.Map<fieldId, json> + `votes` Y.Map<userId, ±1>
 *                                   + `body` Y.XmlFragment (rich text, bound to TipTap)
 *   polls        Y.Map<id, Y.Map>   scalar keys (options stored as JSON) + `votes` Y.Map<userId, PollVote>
 *   boards       Y.Map<id, Y.Map>   scalar keys + `items` Y.Map<itemId, Y.Map> (notes, labels, shapes,
 *                                   ink and images: scalars + `votes` Y.Map)
 *   people       Y.Map<userId, Person>
 *
 * Values that are edited as a whole (field defs, views, select values, relation arrays) are plain
 * JSON with last-writer-wins semantics; things edited concurrently at a finer grain (record keys,
 * votes, board items, rich text) are nested shared types so concurrent edits merge.
 */

export const SCHEMA_VERSION = 1;

/** Origin of user-initiated local mutations — tracked by the undo manager. */
export const LOCAL_ORIGIN = Symbol('tomo.local');
/** Origin of automatic bookkeeping (timestamps, profiles, migrations) — never undoable. */
export const SYSTEM_ORIGIN = Symbol('tomo.system');

type YMap = Y.Map<unknown>;

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const numOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const color = (v: unknown, fallback: ColorName): ColorName => (isColorName(v) ? v : fallback);
/** No list at all means a document written before sections, which had every one of them. */
const sectionList = (v: unknown): ProjectSection[] => (Array.isArray(v) ? v.filter(isProjectSection) : [...PROJECT_SECTIONS]);
const BOARD_ITEM_TYPES: BoardItem['type'][] = ['note', 'text', 'shape', 'draw', 'image'];
/** An item written by a newer version falls back to a note, so an old client still shows something. */
const itemType = (v: unknown): BoardItem['type'] => (BOARD_ITEM_TYPES.includes(v as BoardItem['type']) ? (v as BoardItem['type']) : 'note');
const shape = (v: unknown): BoardShape => (BOARD_SHAPES.includes(v as BoardShape) ? (v as BoardShape) : 'rect');
/** Ink points survive a round trip through JSON, so guard the shape and drop anything not finite. */
const points = (v: unknown): number[] => (Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number' && Number.isFinite(n)) : []);
const file = (v: unknown): FileMeta | null => (v && typeof v === 'object' && typeof (v as FileMeta).hash === 'string' ? (v as FileMeta) : null);
/** One vote per person: a dot total from when boards had a budget reads back as a single vote. */
function voteSet(m: Y.Map<number> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  m?.forEach((v, k) => {
    if (v > 0) out[k] = 1;
  });
  return out;
}

const BODY_TOUCH_INTERVAL = 20_000;

/** Shared empty result so snapshot getters stay referentially stable. */
const NO_LINKS: readonly string[] = Object.freeze([]) as readonly string[];

/** Keys changed on a root map event (observeDeep hands us the base YEvent type). */
const keysOf = (e: Y.YEvent<any>): Set<string> => (e as Y.YMapEvent<unknown>).keysChanged;

export interface NewField {
  id?: string;
  name: string;
  type: FieldDef['type'];
  config?: FieldDef['config'];
  description?: string;
  role?: FieldDef['role'];
}

export interface NewView extends Partial<Omit<ViewDef, 'id' | 'name' | 'type'>> {
  id?: string;
  name: string;
  type: ViewDef['type'];
}

export interface NewCollection {
  id?: string;
  name: string;
  icon?: string;
  color?: ColorName;
  description?: string;
  titleLabel?: string;
  /** Put it straight into a group. */
  groupId?: string | null;
  fields?: NewField[];
  views?: NewView[];
  order?: string;
}

export interface NewRecord {
  id?: string;
  title?: string;
  icon?: string | null;
  values?: Record<string, unknown>;
  parentId?: string | null;
  cover?: FileMeta | null;
  order?: string;
  position?: 'start' | 'end';
  body?: RichNode[];
  votes?: Record<string, number>;
  createdBy?: string | null;
  createdAt?: number;
}

export interface NewPoll {
  id?: string;
  question: string;
  description?: string;
  mode?: PollData['mode'];
  options: (Omit<PollOption, 'id'> & { id?: string })[];
  anonymous?: boolean;
  closesAt?: number | null;
  linkedRecordId?: string | null;
}

export interface NewBoardItem {
  id?: string;
  type?: BoardItem['type'];
  x: number;
  y: number;
  w?: number;
  h?: number;
  color?: ColorName;
  text?: string;
  createdBy?: string | null;
  votes?: Record<string, number>;
  shape?: BoardShape;
  points?: readonly number[];
  strokeWidth?: number;
  filled?: boolean;
  file?: FileMeta | null;
}

export function defaultView(partial: NewView): ViewDef {
  return {
    id: partial.id ?? uid(),
    name: partial.name,
    type: partial.type,
    order: partial.order ?? 'a0',
    sort: partial.sort ?? [],
    filters: partial.filters ?? [],
    filterMode: partial.filterMode ?? 'and',
    groupBy: partial.groupBy ?? null,
    hiddenFields: partial.hiddenFields ?? [],
    fieldWidths: partial.fieldWidths ?? {},
    coverFieldId: partial.coverFieldId ?? null,
    cardSize: partial.cardSize ?? 'md',
  };
}

function normalizeView(v: Partial<ViewDef> & { id: string }): ViewDef {
  return defaultView({ ...v, name: v.name ?? 'View', type: v.type ?? 'table' } as NewView & { id: string });
}

function normalizeField(f: Partial<FieldDef> & { id: string }): FieldDef {
  const type = f.type && FIELD_TYPES[f.type] ? f.type : 'text';
  const out: FieldDef = { id: f.id, name: f.name ?? 'Field', type, order: f.order ?? 'a0', config: f.config ?? {} };
  if (f.description) out.description = f.description;
  if (f.role) out.role = f.role;
  return out;
}

export class ProjectDoc {
  readonly doc: Y.Doc;
  readonly meta: YMap;
  readonly collections: Y.Map<YMap>;
  /** The sidebar's groups. Small and edited whole, like a field definition, so plain JSON. */
  readonly groups: Y.Map<CollectionGroup>;
  readonly records: Y.Map<YMap>;
  readonly polls: Y.Map<YMap>;
  readonly boards: Y.Map<YMap>;
  readonly people: Y.Map<Person>;
  readonly undoManager: Y.UndoManager;

  /** Who is making changes (stamped on createdBy / updatedBy / votes). */
  actorId: string | null = null;

  /** Called when a user action is refused because the document is read-only. */
  onReadOnlyWrite: (() => void) | null = null;
  private readOnlyFlag = false;

  /** Bumped after every transaction that changed anything. */
  version = 0;
  /** Bumped whenever any record body changed (search index / backlinks). */
  textVersion = 0;

  private listeners = new Set<() => void>();
  private dirty = false;

  private metaCache: ProjectMeta | null = null;
  private collectionCache = new Map<string, CollectionData>();
  private collectionList: CollectionData[] | null = null;
  private groupList: CollectionGroup[] | null = null;
  private tree: CollectionTreeNode[] | null = null;
  private recordCache = new Map<string, RecordData>();
  private recordLists = new Map<string, RecordData[]>();
  private liveRecords: RecordData[] | null = null;
  private pollCache = new Map<string, PollData>();
  private pollList: PollData[] | null = null;
  private boardCache = new Map<string, BoardData>();
  private boardList: BoardData[] | null = null;
  private boardItems = new Map<string, BoardItem[]>();
  private peopleList: Person[] | null = null;
  private textCache = new Map<string, { v: number; value: BodyText }>();
  private bodyVersions = new Map<string, number>();
  private lastTouch = new Map<string, number>();
  private backlinkCache: { key: string; map: Map<string, string[]> } | null = null;

  constructor(doc: Y.Doc) {
    this.doc = doc;
    this.meta = doc.getMap('meta');
    this.collections = doc.getMap('collections');
    this.groups = doc.getMap('groups');
    this.records = doc.getMap('records');
    this.polls = doc.getMap('polls');
    this.boards = doc.getMap('boards');
    this.people = doc.getMap('people');

    this.meta.observe(() => {
      this.metaCache = null;
      this.dirty = true;
    });
    this.people.observe(() => {
      this.peopleList = null;
      this.dirty = true;
    });
    this.groups.observe(() => {
      this.groupList = null;
      this.tree = null;
      this.dirty = true;
    });
    this.collections.observeDeep((events) => {
      for (const e of events) {
        if (e.target === this.collections) for (const key of keysOf(e)) this.invalidateCollection(key);
        else this.invalidateCollection(String(e.path[0]));
      }
      this.dirty = true;
    });
    this.records.observeDeep((events, txn) => {
      for (const e of events) {
        if (e.target === this.records) {
          for (const key of keysOf(e)) this.invalidateRecord(key);
          continue;
        }
        const id = String(e.path[0]);
        if (e.path[1] === 'body') {
          this.bodyVersions.set(id, (this.bodyVersions.get(id) ?? 0) + 1);
          this.textVersion++;
          if (txn.local && txn.origin !== LOCAL_ORIGIN && txn.origin !== SYSTEM_ORIGIN) this.scheduleTouch(id);
          continue;
        }
        this.invalidateRecord(id);
      }
      this.dirty = true;
    });
    this.polls.observeDeep((events) => {
      for (const e of events) {
        if (e.target === this.polls) for (const key of keysOf(e)) this.pollCache.delete(key);
        else this.pollCache.delete(String(e.path[0]));
      }
      this.pollList = null;
      this.dirty = true;
    });
    this.boards.observeDeep((events) => {
      for (const e of events) {
        if (e.target === this.boards) {
          for (const key of keysOf(e)) {
            this.boardCache.delete(key);
            this.boardItems.delete(key);
          }
          continue;
        }
        const id = String(e.path[0]);
        if (e.path[1] === 'items') this.boardItems.delete(id);
        else this.boardCache.delete(id);
      }
      this.boardList = null;
      this.dirty = true;
    });
    doc.on('afterTransaction', this.onAfterTransaction);

    this.undoManager = new Y.UndoManager([this.meta, this.collections, this.groups, this.records, this.polls, this.boards], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 500,
    });
  }

  private onAfterTransaction = () => {
    if (!this.dirty) return;
    this.dirty = false;
    this.version++;
    for (const l of [...this.listeners]) l();
  };

  /** Subscribe to any change (React: pair with `useSyncExternalStore` + the cached getters). */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  destroy() {
    this.doc.off('afterTransaction', this.onAfterTransaction);
    this.undoManager.destroy();
    this.listeners.clear();
  }

  /**
   * Inside a transaction, observers have not run yet, so cached snapshots may be stale: getters
   * re-read the document instead (observers invalidate everything that changed afterwards).
   * Yjs clears `doc._transaction` before calling observers, so renders always hit the caches.
   */
  private get inTxn(): boolean {
    return this.doc._transaction !== null;
  }

  /**
   * Viewers of a cloud project can't change it: the server drops their updates, so writing
   * locally would only fork their copy. While read-only every mutation is refused (user actions
   * report through `onReadOnlyWrite`, background bookkeeping is skipped silently).
   */
  get readOnly(): boolean {
    return this.readOnlyFlag;
  }

  setReadOnly(value: boolean) {
    if (value === this.readOnlyFlag) return;
    this.readOnlyFlag = value;
    this.version++;
    for (const l of [...this.listeners]) l();
  }

  /** Run user mutations in one undoable transaction. */
  transact<T>(fn: () => T): T {
    if (this.readOnlyFlag) {
      this.onReadOnlyWrite?.();
      return undefined as T;
    }
    let out!: T;
    this.doc.transact(() => {
      out = fn();
    }, LOCAL_ORIGIN);
    return out;
  }

  private system(fn: () => void) {
    if (this.readOnlyFlag) return;
    this.doc.transact(fn, SYSTEM_ORIGIN);
  }

  /* ============================================================================================
   * Cache invalidation
   * ========================================================================================== */

  private invalidateCollection(id: string) {
    this.collectionCache.delete(id);
    this.collectionList = null;
    this.tree = null;
    this.liveRecords = null;
  }

  private invalidateRecord(id: string) {
    const old = this.recordCache.get(id);
    this.recordCache.delete(id);
    if (old) this.recordLists.delete(old.collectionId);
    const cid = this.records.get(id)?.get('collectionId');
    if (typeof cid === 'string') this.recordLists.delete(cid);
    this.liveRecords = null;
    this.backlinkCache = null;
  }

  private scheduleTouch(recordId: string) {
    const last = this.lastTouch.get(recordId) ?? 0;
    const t = now();
    if (t - last < BODY_TOUCH_INTERVAL) return;
    this.lastTouch.set(recordId, t);
    // Can't write inside an observer's transaction; defer to a microtask.
    queueMicrotask(() => {
      const y = this.records.get(recordId);
      if (!y) return;
      this.system(() => {
        y.set('updatedAt', now());
        if (this.actorId) y.set('updatedBy', this.actorId);
      });
    });
  }

  /* ============================================================================================
   * Meta
   * ========================================================================================== */

  getMeta(): ProjectMeta {
    if (this.inTxn) this.metaCache = null;
    if (!this.metaCache) {
      const m = this.meta;
      this.metaCache = {
        id: str(m.get('id')),
        name: str(m.get('name'), 'Untitled project'),
        description: str(m.get('description')),
        icon: str(m.get('icon'), 'i:gem'),
        color: color(m.get('color'), 'yellow'),
        createdAt: num(m.get('createdAt')),
        schemaVersion: num(m.get('schemaVersion'), SCHEMA_VERSION),
        template: str(m.get('template'), 'blank'),
        sections: sectionList(m.get('sections')),
      };
    }
    return this.metaCache;
  }

  isInitialized(): boolean {
    return typeof this.meta.get('id') === 'string';
  }

  /** Stamp a brand-new document. Safe to call only once per project. */
  initialize(meta: Pick<ProjectMeta, 'id' | 'name'> & Partial<ProjectMeta>) {
    this.system(() => {
      this.meta.set('id', meta.id);
      this.meta.set('name', meta.name);
      this.meta.set('description', meta.description ?? '');
      this.meta.set('icon', meta.icon ?? 'i:gem');
      this.meta.set('color', meta.color ?? 'yellow');
      this.meta.set('createdAt', meta.createdAt ?? now());
      this.meta.set('schemaVersion', SCHEMA_VERSION);
      this.meta.set('template', meta.template ?? 'blank');
      this.meta.set('sections', [...(meta.sections ?? PROJECT_SECTIONS)]);
    });
  }

  updateMeta(patch: Partial<Pick<ProjectMeta, 'name' | 'description' | 'icon' | 'color'>>) {
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (v !== undefined) this.meta.set(k, v);
    });
  }

  hasSection(section: ProjectSection): boolean {
    return this.getMeta().sections.includes(section);
  }

  /**
   * Add or remove a section. Removing only hides it: its boards or polls stay in the document, so
   * adding it again brings them back as they were.
   */
  setSection(section: ProjectSection, on: boolean) {
    const current = this.getMeta().sections;
    if (current.includes(section) === on) return;
    // Rebuilt from the canonical list so the order never depends on when things were added.
    const next = on ? PROJECT_SECTIONS.filter((s) => s === section || current.includes(s)) : current.filter((s) => s !== section);
    this.transact(() => this.meta.set('sections', [...next]));
  }

  /* ============================================================================================
   * People
   * ========================================================================================== */

  listPeople(): Person[] {
    if (this.inTxn) this.peopleList = null;
    if (!this.peopleList) {
      this.peopleList = [...this.people.values()]
        .filter((p) => p && typeof p.id === 'string')
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return this.peopleList;
  }

  getPerson(id: string | null | undefined): Person | undefined {
    return id ? this.people.get(id) : undefined;
  }

  /** Keep the local user's profile in the doc so other copies can render it offline. */
  upsertPerson(person: Person) {
    const cur = this.people.get(person.id);
    if (cur && cur.name === person.name && cur.color === person.color && !!cur.sample === !!person.sample) return;
    this.system(() => this.people.set(person.id, { ...person }));
  }

  /** Rewrite every reference to `fromId` (votes, authorship, person fields) — used when a local
   * profile signs in and becomes an account id. */
  replaceActorId(fromId: string, toId: string) {
    if (fromId === toId) return;
    this.system(() => {
      const p = this.people.get(fromId);
      if (p) {
        this.people.delete(fromId);
        if (!this.people.get(toId)) this.people.set(toId, { ...p, id: toId });
      }
      const swapMapKey = (m: YMap | undefined) => {
        if (!m || !m.has(fromId)) return;
        const v = m.get(fromId);
        m.delete(fromId);
        if (!m.has(toId)) m.set(toId, v);
      };
      const personFields = new Map<string, Set<string>>();
      for (const c of this.listCollections(true)) personFields.set(c.id, new Set(c.fields.filter((f) => f.type === 'person').map((f) => f.id)));
      this.records.forEach((y) => {
        if (y.get('createdBy') === fromId) y.set('createdBy', toId);
        if (y.get('updatedBy') === fromId) y.set('updatedBy', toId);
        swapMapKey(y.get('votes') as YMap);
        const values = y.get('values') as YMap | undefined;
        const fids = personFields.get(str(y.get('collectionId')));
        if (values && fids) {
          for (const fid of fids) {
            const v = values.get(fid);
            if (Array.isArray(v) && v.includes(fromId)) values.set(fid, [...new Set(v.map((x) => (x === fromId ? toId : x)))]);
          }
        }
      });
      this.polls.forEach((y) => {
        if (y.get('createdBy') === fromId) y.set('createdBy', toId);
        swapMapKey(y.get('votes') as YMap);
      });
      this.boards.forEach((b) => {
        if (b.get('createdBy') === fromId) b.set('createdBy', toId);
        (b.get('items') as Y.Map<YMap> | undefined)?.forEach((it) => {
          if (it.get('createdBy') === fromId) it.set('createdBy', toId);
          swapMapKey(it.get('votes') as YMap);
        });
      });
    });
  }

  /* ============================================================================================
   * Collections
   * ========================================================================================== */

  private readCollection(y: YMap): CollectionData {
    const fieldsY = y.get('fields') as Y.Map<FieldDef> | undefined;
    const viewsY = y.get('views') as Y.Map<ViewDef> | undefined;
    // Documents written when collections had kinds still carry one; it means nothing now, and
    // everything those collections hold — fields, views, records — is ordinary and carries on.
    const label = str(y.get('titleLabel'), 'Name');
    return {
      id: str(y.get('id')),
      name: str(y.get('name'), 'Untitled'),
      icon: str(y.get('icon'), 'i:box'),
      color: color(y.get('color'), 'gray'),
      description: str(y.get('description')),
      // Writing used to label its records "Title", which reads badly as a noun ("New title").
      titleLabel: label === 'Title' ? 'Doc' : label,
      groupId: strOrNull(y.get('groupId')),
      order: str(y.get('order'), 'a0'),
      createdAt: num(y.get('createdAt')),
      deletedAt: numOrNull(y.get('deletedAt')),
      fields: fieldsY ? sortByOrder([...fieldsY.values()].filter(Boolean).map(normalizeField)) : [],
      views: viewsY ? sortByOrder([...viewsY.values()].filter(Boolean).map(normalizeView)) : [],
    };
  }

  getCollection(id: string | null | undefined): CollectionData | undefined {
    if (!id) return undefined;
    if (this.inTxn) this.collectionCache.delete(id);
    let c = this.collectionCache.get(id);
    if (!c) {
      const y = this.collections.get(id);
      if (!y) return undefined;
      c = this.readCollection(y);
      this.collectionCache.set(id, c);
    }
    return c;
  }

  /** Live collections in sidebar order (pass `true` to include trashed ones). */
  listCollections(includeDeleted = false): CollectionData[] {
    if (this.inTxn) this.collectionList = null;
    if (includeDeleted) return sortByOrder([...this.collections.keys()].map((id) => this.getCollection(id)!).filter(Boolean));
    if (!this.collectionList) {
      this.collectionList = sortByOrder(
        [...this.collections.keys()].map((id) => this.getCollection(id)!).filter((c) => c && c.deletedAt === null),
      );
    }
    return this.collectionList;
  }

  createCollection(input: NewCollection): string {
    const id = input.id ?? uid();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      this.collections.set(id, y);
      y.set('id', id);
      y.set('name', input.name);
      y.set('icon', input.icon ?? 'i:box');
      y.set('color', input.color ?? 'gray');
      y.set('description', input.description ?? '');
      if (input.titleLabel) y.set('titleLabel', input.titleLabel);
      const groupId = this.groupOf(input.groupId);
      y.set('groupId', groupId);
      y.set('order', input.order ?? orderAfterLast(this.collectionSiblings(groupId)));
      y.set('createdAt', now());
      y.set('deletedAt', null);
      const fieldsY = new Y.Map<FieldDef>();
      const viewsY = new Y.Map<ViewDef>();
      y.set('fields', fieldsY);
      y.set('views', viewsY);
      let prev: string | null = null;
      for (const f of input.fields ?? []) {
        const order = orderBetween(prev, null);
        prev = order;
        const def: FieldDef = { id: f.id ?? uid(), name: f.name, type: f.type, order, config: { ...defaultFieldConfig(f.type), ...f.config } };
        if (f.description) def.description = f.description;
        if (f.role) def.role = f.role;
        fieldsY.set(def.id, def);
      }
      prev = null;
      const views = input.views?.length ? input.views : [{ name: 'All', type: 'table' as const }];
      for (const v of views) {
        const order = orderBetween(prev, null);
        prev = order;
        const def = defaultView({ ...v, order });
        viewsY.set(def.id, def);
      }
    });
    return id;
  }

  updateCollection(id: string, patch: Partial<Pick<CollectionData, 'name' | 'icon' | 'color' | 'description' | 'titleLabel' | 'order'>>) {
    const y = this.collections.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (v !== undefined) y.set(k, v);
    });
  }

  /** Rename a collection. A record label taken from the old name ("Recipes" → "Recipe") follows the new one. */
  renameCollection(id: string, name: string) {
    const c = this.getCollection(id);
    const next = name.trim();
    if (!c || !next || next === c.name) return;
    const derived = c.titleLabel === singularize(c.name);
    this.updateCollection(id, derived ? { name: next, titleLabel: singularize(next) } : { name: next });
  }

  /**
   * Move a collection to `toIndex` among the things it sits beside: the other collections in its
   * group, or — at the top level — the loose collections and the groups together. Pass `groupId`
   * to move it into another group (`null` takes it out of the one it is in).
   */
  moveCollection(id: string, toIndex: number, groupId?: string | null) {
    const c = this.getCollection(id);
    const y = this.collections.get(id);
    if (!c || !y) return;
    // A group that is gone leaves the collection at the top level, and the move tidies that up.
    const target = this.groupOf(groupId === undefined ? c.groupId : groupId);
    const rest = this.collectionSiblings(target).filter((s) => s.id !== id);
    const i = Math.max(0, Math.min(toIndex, rest.length));
    const order = orderBetween(rest[i - 1]?.order ?? null, rest[i]?.order ?? null);
    this.transact(() => {
      if (target !== c.groupId) y.set('groupId', target);
      y.set('order', order);
    });
  }

  /** Put a collection in a group (`null` = on its own), after everything already there. */
  setCollectionGroup(id: string, groupId: string | null) {
    const c = this.getCollection(id);
    if (!c) return;
    const target = this.groupOf(groupId);
    if (target === this.groupOf(c.groupId)) return;
    this.moveCollection(id, Number.MAX_SAFE_INTEGER, target);
  }

  /** Move to trash (records stay attached and come back on restore). */
  trashCollection(id: string) {
    this.collections.get(id) && this.transact(() => this.collections.get(id)!.set('deletedAt', now()));
  }

  restoreCollection(id: string) {
    this.collections.get(id) && this.transact(() => this.collections.get(id)!.set('deletedAt', null));
  }

  /** Permanently delete a collection and all of its records. */
  destroyCollection(id: string) {
    this.transact(() => {
      for (const [rid, y] of [...this.records.entries()]) if (y.get('collectionId') === id) this.records.delete(rid);
      this.collections.delete(id);
    });
  }

  /* ---------------------------------------------------------------------------------------- *
   * Groups (the sidebar's folders)
   * ---------------------------------------------------------------------------------------- */

  /** Read through the cached list, so the same group is the same object between renders. */
  getCollectionGroup(id: string | null | undefined): CollectionGroup | undefined {
    return id ? this.listCollectionGroups().find((g) => g.id === id) : undefined;
  }

  /** Groups in sidebar order. */
  listCollectionGroups(): CollectionGroup[] {
    if (this.inTxn) this.groupList = null;
    if (!this.groupList) {
      this.groupList = sortByOrder(
        [...this.groups.values()]
          .filter((g) => g && typeof g.id === 'string')
          // A group written by another version may be missing pieces; read it the way the rest is read.
          .map((g) => ({ id: g.id, name: str(g.name, 'Group'), order: str(g.order, 'a0'), createdAt: num(g.createdAt) })),
      );
    }
    return this.groupList;
  }

  /**
   * The collection list as the sidebar draws it: loose collections and groups in one order, each
   * group carrying its own collections. A collection whose group is gone reads as a loose one.
   */
  collectionTree(): CollectionTreeNode[] {
    if (this.inTxn) this.tree = null;
    if (!this.tree) {
      const groups = this.listCollectionGroups();
      const inside = new Map<string, CollectionData[]>(groups.map((g) => [g.id, []]));
      const nodes: CollectionTreeNode[] = [];
      for (const c of this.listCollections()) {
        const bucket = c.groupId ? inside.get(c.groupId) : undefined;
        if (bucket) bucket.push(c);
        else nodes.push({ kind: 'collection', id: c.id, order: c.order, collection: c });
      }
      for (const g of groups) nodes.push({ kind: 'group', id: g.id, order: g.order, group: g, collections: inside.get(g.id)! });
      this.tree = sortByOrder(nodes);
    }
    return this.tree;
  }

  /**
   * What an item at `groupId` is ordered against: the collections of that group, or — at the top
   * level — the loose collections and the groups together, since both share one order.
   */
  private collectionSiblings(groupId: string | null): Ordered[] {
    if (groupId) return this.listCollections().filter((c) => c.groupId === groupId);
    return this.collectionTree();
  }

  /** The group an id names, or `null` — a collection can outlive the group it pointed at. */
  private groupOf(id: string | null | undefined): string | null {
    return id && this.groups.has(id) ? id : null;
  }

  createCollectionGroup(input: { id?: string; name: string; order?: string } = { name: 'New group' }): string {
    const id = input.id ?? uid();
    const group: CollectionGroup = {
      id,
      name: input.name.trim() || 'New group',
      order: input.order ?? orderAfterLast(this.collectionTree()),
      createdAt: now(),
    };
    this.transact(() => this.groups.set(id, group));
    return id;
  }

  renameCollectionGroup(id: string, name: string) {
    const g = this.getCollectionGroup(id);
    const next = name.trim();
    if (!g || !next || next === g.name) return;
    this.transact(() => this.groups.set(id, { ...g, name: next }));
  }

  /** Move a group to `toIndex` among the top-level rows (groups and loose collections). */
  moveCollectionGroup(id: string, toIndex: number) {
    const g = this.getCollectionGroup(id);
    if (!g) return;
    const rest = this.collectionTree().filter((n) => n.id !== id);
    const order = orderAtIndex(rest, toIndex);
    this.transact(() => this.groups.set(id, { ...g, order }));
  }

  /**
   * Delete a group. Nothing inside is deleted: its collections take the group's place in the list,
   * in the order they had, so the list reads the same with the folder opened out.
   */
  deleteCollectionGroup(id: string) {
    const g = this.getCollectionGroup(id);
    if (!g) return;
    const top = this.collectionTree();
    const next = top[top.findIndex((n) => n.id === id) + 1];
    // The group's own key is freed by the delete, so the gap after it is ours to fill.
    const upper = next && next.order > g.order ? next.order : null;
    const inside = sortByOrder([...this.collections.keys()].map((cid) => this.getCollection(cid)!).filter((c) => c?.groupId === id));
    const keys = ordersBetween(g.order, upper, inside.length);
    this.transact(() => {
      inside.forEach((c, i) => {
        const y = this.collections.get(c.id)!;
        y.set('groupId', null);
        y.set('order', keys[i]!);
      });
      this.groups.delete(id);
    });
  }

  /* ---------------------------------------------------------------------------------------- *
   * Fields
   * ---------------------------------------------------------------------------------------- */

  private fieldsY(collectionId: string) {
    return this.collections.get(collectionId)?.get('fields') as Y.Map<FieldDef> | undefined;
  }

  private viewsY(collectionId: string) {
    return this.collections.get(collectionId)?.get('views') as Y.Map<ViewDef> | undefined;
  }

  getField(collectionId: string, fieldId: string): FieldDef | undefined {
    return this.getCollection(collectionId)?.fields.find((f) => f.id === fieldId);
  }

  addField(collectionId: string, input: NewField & { after?: string | null }): string {
    const fieldsY = this.fieldsY(collectionId);
    const c = this.getCollection(collectionId);
    if (!fieldsY || !c) throw new Error(`Unknown collection ${collectionId}`);
    const id = input.id ?? uid();
    let order: string;
    if (input.after !== undefined) {
      const idx = input.after === null ? -1 : c.fields.findIndex((f) => f.id === input.after);
      order = orderBetween(c.fields[idx]?.order ?? null, c.fields[idx + 1]?.order ?? null);
    } else order = orderAfterLast(c.fields);
    const def: FieldDef = { id, name: input.name, type: input.type, order, config: { ...defaultFieldConfig(input.type), ...input.config } };
    if (input.description) def.description = input.description;
    if (input.role) def.role = input.role;
    this.transact(() => fieldsY.set(id, def));
    return id;
  }

  /**
   * Update a field definition. Changing the `type` converts every record's value so data survives
   * (e.g. text → tags creates one option per distinct value).
   */
  updateField(collectionId: string, fieldId: string, patch: Partial<Omit<FieldDef, 'id'>>) {
    const fieldsY = this.fieldsY(collectionId);
    const old = fieldsY?.get(fieldId);
    if (!fieldsY || !old) return;
    const next: FieldDef = normalizeField({ ...old, ...patch, id: fieldId, config: { ...old.config, ...patch.config } });
    this.transact(() => {
      if (patch.type && patch.type !== old.type) {
        next.config = { ...defaultFieldConfig(patch.type), ...patch.config };
        const records = this.listRecords(collectionId, true);
        if ((next.type === 'select' || next.type === 'multiSelect') && !patch.config?.options) {
          const labels = records.map((r) => valueToText(r, old, this.valueContext())).filter(Boolean);
          next.config.options = optionsFromValues(labels, () => uid(8), old.config.options ?? []);
        }
        for (const r of records) {
          const values = this.records.get(r.id)?.get('values') as YMap | undefined;
          if (!values || !values.has(fieldId)) continue;
          const converted = convertValue(values.get(fieldId), old, next, r, this.valueContext());
          if (converted === undefined) values.delete(fieldId);
          else values.set(fieldId, converted);
        }
        if (!FIELD_TYPES[next.type].groupable) this.forEachView(collectionId, (v) => (v.groupBy === fieldId ? { ...v, groupBy: null } : v));
        this.forEachView(collectionId, (v) =>
          v.filters.some((f) => f.fieldId === fieldId) ? { ...v, filters: v.filters.filter((f) => f.fieldId !== fieldId) } : v,
        );
      }
      fieldsY.set(fieldId, next);
    });
  }

  removeField(collectionId: string, fieldId: string) {
    const fieldsY = this.fieldsY(collectionId);
    if (!fieldsY?.has(fieldId)) return;
    this.transact(() => {
      fieldsY.delete(fieldId);
      this.records.forEach((y) => {
        if (y.get('collectionId') === collectionId) (y.get('values') as YMap | undefined)?.delete(fieldId);
      });
      this.forEachView(collectionId, (v) => ({
        ...v,
        sort: v.sort.filter((s) => s.fieldId !== fieldId),
        filters: v.filters.filter((f) => f.fieldId !== fieldId),
        hiddenFields: v.hiddenFields.filter((h) => h !== fieldId),
        groupBy: v.groupBy === fieldId ? null : v.groupBy,
        coverFieldId: v.coverFieldId === fieldId ? null : v.coverFieldId,
      }));
    });
  }

  moveField(collectionId: string, fieldId: string, toIndex: number) {
    const c = this.getCollection(collectionId);
    const fieldsY = this.fieldsY(collectionId);
    const f = fieldsY?.get(fieldId);
    if (!c || !fieldsY || !f) return;
    const rest = c.fields.filter((x) => x.id !== fieldId);
    const i = Math.max(0, Math.min(toIndex, rest.length));
    const order = orderBetween(rest[i - 1]?.order ?? null, rest[i]?.order ?? null);
    this.transact(() => fieldsY.set(fieldId, { ...f, order }));
  }

  /** Append an option to a select/tags field and return its id. */
  addSelectOption(collectionId: string, fieldId: string, label: string, optionColor?: ColorName): string {
    const f = this.fieldsY(collectionId)?.get(fieldId);
    if (!f) throw new Error('Unknown field');
    const options = f.config.options ?? [];
    const existing = options.find((o) => o.label.toLowerCase() === label.trim().toLowerCase());
    if (existing) return existing.id;
    const palette: ColorName[] = ['blue', 'green', 'orange', 'purple', 'pink', 'teal', 'yellow', 'red', 'brown', 'gray'];
    const opt = { id: uid(8), label: label.trim(), color: optionColor ?? palette[options.length % palette.length]! };
    this.updateField(collectionId, fieldId, { config: { ...f.config, options: [...options, opt] } });
    return opt.id;
  }

  /* ---------------------------------------------------------------------------------------- *
   * Views
   * ---------------------------------------------------------------------------------------- */

  private forEachView(collectionId: string, fn: (v: ViewDef) => ViewDef) {
    const viewsY = this.viewsY(collectionId);
    if (!viewsY) return;
    for (const [id, v] of [...viewsY.entries()]) {
      const next = fn(normalizeView(v));
      if (next !== v) viewsY.set(id, next);
    }
  }

  addView(collectionId: string, input: NewView): string {
    const viewsY = this.viewsY(collectionId);
    const c = this.getCollection(collectionId);
    if (!viewsY || !c) throw new Error('Unknown collection');
    const def = defaultView({ ...input, order: input.order ?? orderAfterLast(c.views) });
    this.transact(() => viewsY.set(def.id, def));
    return def.id;
  }

  updateView(collectionId: string, viewId: string, patch: Partial<Omit<ViewDef, 'id'>>) {
    const viewsY = this.viewsY(collectionId);
    const v = viewsY?.get(viewId);
    if (!viewsY || !v) return;
    this.transact(() => viewsY.set(viewId, { ...normalizeView(v), ...patch, id: viewId }));
  }

  removeView(collectionId: string, viewId: string) {
    const viewsY = this.viewsY(collectionId);
    if (!viewsY || viewsY.size <= 1) return;
    this.transact(() => viewsY.delete(viewId));
  }

  /** Move a view to `toIndex` in the collection's tab strip. */
  moveView(collectionId: string, viewId: string, toIndex: number) {
    const c = this.getCollection(collectionId);
    if (!c?.views.some((v) => v.id === viewId)) return;
    this.updateView(collectionId, viewId, { order: orderAtIndex(c.views.filter((v) => v.id !== viewId), toIndex) });
  }

  duplicateView(collectionId: string, viewId: string): string | null {
    const c = this.getCollection(collectionId);
    const v = c?.views.find((x) => x.id === viewId);
    if (!c || !v) return null;
    const idx = c.views.indexOf(v);
    return this.addView(collectionId, {
      ...v,
      id: uid(),
      name: `${v.name} copy`,
      order: orderBetween(v.order, c.views[idx + 1]?.order ?? null),
    });
  }

  /* ============================================================================================
   * Records
   * ========================================================================================== */

  private readRecord(y: YMap): RecordData {
    const values = y.get('values') as YMap | undefined;
    const votes = y.get('votes') as Y.Map<number> | undefined;
    return {
      id: str(y.get('id')),
      collectionId: str(y.get('collectionId')),
      title: str(y.get('title')),
      icon: strOrNull(y.get('icon')),
      order: str(y.get('order'), 'a0'),
      parentId: strOrNull(y.get('parentId')),
      cover: file(y.get('cover')),
      values: values ? (values.toJSON() as Record<string, unknown>) : {},
      votes: votes ? (votes.toJSON() as Record<string, number>) : {},
      createdAt: num(y.get('createdAt')),
      createdBy: strOrNull(y.get('createdBy')),
      updatedAt: num(y.get('updatedAt')),
      updatedBy: strOrNull(y.get('updatedBy')),
      deletedAt: numOrNull(y.get('deletedAt')),
    };
  }

  getRecord(id: string | null | undefined): RecordData | undefined {
    if (!id) return undefined;
    if (this.inTxn) this.recordCache.delete(id);
    let r = this.recordCache.get(id);
    if (!r) {
      const y = this.records.get(id);
      if (!y) return undefined;
      r = this.readRecord(y);
      this.recordCache.set(id, r);
    }
    return r;
  }

  /** The shared rich-text body of a record (bind TipTap to it). */
  getBody(id: string): Y.XmlFragment | undefined {
    return this.records.get(id)?.get('body') as Y.XmlFragment | undefined;
  }

  /** Records of a collection in manual order (trashed ones excluded unless asked). */
  listRecords(collectionId: string, includeDeleted = false): RecordData[] {
    if (this.inTxn) this.recordLists.delete(collectionId);
    if (includeDeleted) {
      const all: RecordData[] = [];
      this.records.forEach((y, id) => {
        if (y.get('collectionId') === collectionId) all.push(this.getRecord(id)!);
      });
      return all.sort(compareOrder);
    }
    let list = this.recordLists.get(collectionId);
    if (!list) {
      list = [];
      this.records.forEach((y, id) => {
        if (y.get('collectionId') !== collectionId) return;
        const r = this.getRecord(id)!;
        if (r.deletedAt === null) list!.push(r);
      });
      list.sort(compareOrder);
      this.recordLists.set(collectionId, list);
    }
    return list;
  }

  /** Every live record of every live collection (search, mentions, relation pickers). */
  allRecords(): RecordData[] {
    if (this.inTxn) this.liveRecords = null;
    if (!this.liveRecords) {
      this.liveRecords = this.listCollections().flatMap((c) => this.listRecords(c.id));
    }
    return this.liveRecords;
  }

  /** Records whose collection is live but that are in the trash. */
  trashedRecords(): RecordData[] {
    const out: RecordData[] = [];
    this.records.forEach((_y, id) => {
      const r = this.getRecord(id)!;
      if (r.deletedAt !== null) out.push(r);
    });
    return out.sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
  }

  createRecord(collectionId: string, init: NewRecord = {}): string {
    const c = this.getCollection(collectionId);
    if (!c) throw new Error(`Unknown collection ${collectionId}`);
    const id = init.id ?? uid();
    const siblings = this.listRecords(collectionId);
    const order = init.order ?? (init.position === 'start' ? orderBeforeFirst(siblings) : orderAfterLast(siblings));
    const values: Record<string, unknown> = { ...init.values };
    // Status-like fields start at their first option so new tasks/ideas land in the first column.
    for (const f of c.fields) {
      if (f.role === 'status' && values[f.id] === undefined && f.config.options?.length) values[f.id] = f.config.options[0]!.id;
    }
    this.transact(() => {
      const y = new Y.Map<unknown>();
      this.records.set(id, y);
      const t = init.createdAt ?? now();
      y.set('id', id);
      y.set('collectionId', collectionId);
      y.set('title', init.title ?? '');
      y.set('icon', init.icon ?? null);
      y.set('order', order);
      y.set('parentId', init.parentId ?? null);
      y.set('cover', init.cover ?? null);
      y.set('createdAt', t);
      y.set('createdBy', init.createdBy !== undefined ? init.createdBy : this.actorId);
      y.set('updatedAt', t);
      y.set('updatedBy', init.createdBy !== undefined ? init.createdBy : this.actorId);
      y.set('deletedAt', null);
      const valuesY = new Y.Map<unknown>();
      y.set('values', valuesY);
      for (const [k, v] of Object.entries(values)) if (v !== undefined && v !== null) valuesY.set(k, v);
      const votesY = new Y.Map<number>();
      y.set('votes', votesY);
      for (const [k, v] of Object.entries(init.votes ?? {})) votesY.set(k, v);
      const body = new Y.XmlFragment();
      y.set('body', body);
      if (init.body?.length) writeRich(body, init.body);
    });
    return id;
  }

  private stamp(y: YMap) {
    y.set('updatedAt', now());
    if (this.actorId) y.set('updatedBy', this.actorId);
  }

  updateRecord(id: string, patch: Partial<Pick<RecordData, 'title' | 'icon' | 'parentId' | 'order'>>) {
    const y = this.records.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (v !== undefined) y.set(k, v);
      this.stamp(y);
    });
  }

  /** The record's own picture, shown above it and on its card. `null` takes it off. */
  setCover(recordId: string, cover: FileMeta | null) {
    const y = this.records.get(recordId);
    if (!y) return;
    this.transact(() => {
      y.set('cover', cover);
      this.stamp(y);
    });
  }

  /** Set one value; `undefined`/`null`/`''`/`[]` clears it. */
  setValue(recordId: string, fieldId: string, value: unknown) {
    this.setValues(recordId, { [fieldId]: value });
  }

  setValues(recordId: string, patch: Record<string, unknown>) {
    const y = this.records.get(recordId);
    const values = y?.get('values') as YMap | undefined;
    if (!y || !values) return;
    this.transact(() => {
      for (const [fieldId, value] of Object.entries(patch)) {
        const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
        if (empty) values.delete(fieldId);
        else values.set(fieldId, value);
      }
      this.stamp(y);
    });
  }

  /** Reposition a record, optionally changing values in the same step (board drag & drop). */
  moveRecord(id: string, order: string, values?: Record<string, unknown>) {
    const y = this.records.get(id);
    if (!y) return;
    this.transact(() => {
      y.set('order', order);
      if (values) {
        const valuesY = y.get('values') as YMap;
        for (const [k, v] of Object.entries(values)) {
          if (v === undefined || v === null || (Array.isArray(v) && !v.length)) valuesY.delete(k);
          else valuesY.set(k, v);
        }
      }
      this.stamp(y);
    });
  }

  trashRecord(...ids: string[]) {
    this.transact(() => {
      const t = now();
      for (const id of ids) this.records.get(id)?.set('deletedAt', t);
    });
  }

  restoreRecord(...ids: string[]) {
    this.transact(() => {
      for (const id of ids) this.records.get(id)?.set('deletedAt', null);
    });
  }

  destroyRecord(...ids: string[]) {
    this.transact(() => {
      for (const id of ids) this.records.delete(id);
    });
  }

  duplicateRecord(id: string): string | null {
    const src = this.records.get(id);
    const r = this.getRecord(id);
    if (!src || !r) return null;
    const siblings = this.listRecords(r.collectionId);
    const idx = siblings.findIndex((s) => s.id === id);
    const newId = uid();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      this.records.set(newId, y);
      const t = now();
      y.set('id', newId);
      y.set('collectionId', r.collectionId);
      y.set('title', r.title ? `${r.title} (copy)` : '');
      y.set('icon', r.icon);
      y.set('order', orderBetween(r.order, siblings[idx + 1]?.order ?? null));
      y.set('parentId', r.parentId);
      y.set('cover', r.cover);
      y.set('createdAt', t);
      y.set('createdBy', this.actorId);
      y.set('updatedAt', t);
      y.set('updatedBy', this.actorId);
      y.set('deletedAt', null);
      const valuesY = new Y.Map<unknown>();
      y.set('values', valuesY);
      for (const [k, v] of Object.entries(r.values)) valuesY.set(k, structuredClone(v));
      y.set('votes', new Y.Map<number>());
      const body = src.get('body') as Y.XmlFragment | undefined;
      y.set('body', body ? body.clone() : new Y.XmlFragment());
    });
    return newId;
  }

  /** Cast (+1/-1) or retract (0) the actor's vote on a record. */
  voteRecord(id: string, value: 1 | -1 | 0, actorId = this.actorId) {
    const votes = this.records.get(id)?.get('votes') as Y.Map<number> | undefined;
    if (!votes || !actorId) return;
    this.transact(() => {
      if (value === 0) votes.delete(actorId);
      else votes.set(actorId, value);
    });
  }

  /* ---------------------------------------------------------------------------------------- *
   * Text, mentions and backlinks
   * ---------------------------------------------------------------------------------------- */

  getRecordText(id: string): BodyText {
    const v = this.bodyVersions.get(id) ?? 0;
    const hit = this.textCache.get(id);
    if (hit && hit.v === v) return hit.value;
    const value = readRichText(this.getBody(id));
    this.textCache.set(id, { v, value });
    return value;
  }

  /**
   * Ids of live records referencing `id`, either by an @mention in their body or a relation field.
   */
  backlinks(id: string): readonly string[] {
    const key = `${this.version}:${this.textVersion}`;
    if (!this.backlinkCache || this.backlinkCache.key !== key) {
      const map = new Map<string, string[]>();
      const add = (target: string, from: string) => {
        if (target === from) return;
        const list = map.get(target);
        if (!list) map.set(target, [from]);
        else if (!list.includes(from)) list.push(from);
      };
      for (const c of this.listCollections()) {
        const relFields = c.fields.filter((f) => f.type === 'relation');
        for (const r of this.listRecords(c.id)) {
          for (const m of this.getRecordText(r.id).mentions) add(m, r.id);
          for (const f of relFields) {
            const v = r.values[f.id];
            if (Array.isArray(v)) for (const t of v) if (typeof t === 'string') add(t, r.id);
          }
        }
      }
      this.backlinkCache = { key, map };
    }
    return this.backlinkCache.map.get(id) ?? NO_LINKS;
  }

  valueContext() {
    return {
      recordTitle: (rid: string) => this.getRecord(rid)?.title,
      personName: (pid: string) => this.getPerson(pid)?.name,
    };
  }

  /* ============================================================================================
   * Polls
   * ========================================================================================== */

  private readPoll(y: YMap): PollData {
    const votes = y.get('votes') as Y.Map<PollVote> | undefined;
    const options = y.get('options');
    return {
      id: str(y.get('id')),
      question: str(y.get('question'), 'Untitled poll'),
      description: str(y.get('description')),
      mode: (['single', 'multiple', 'score'].includes(str(y.get('mode'))) ? y.get('mode') : 'single') as PollData['mode'],
      options: Array.isArray(options) ? (options as PollOption[]) : [],
      anonymous: y.get('anonymous') === true,
      status: y.get('status') === 'closed' ? 'closed' : 'open',
      closesAt: numOrNull(y.get('closesAt')),
      decision: strOrNull(y.get('decision')),
      decisionNote: str(y.get('decisionNote')),
      linkedRecordId: strOrNull(y.get('linkedRecordId')),
      order: str(y.get('order'), 'a0'),
      createdAt: num(y.get('createdAt')),
      createdBy: strOrNull(y.get('createdBy')),
      closedAt: numOrNull(y.get('closedAt')),
      deletedAt: numOrNull(y.get('deletedAt')),
      votes: votes ? (votes.toJSON() as Record<string, PollVote>) : {},
    };
  }

  getPoll(id: string | null | undefined): PollData | undefined {
    if (!id) return undefined;
    if (this.inTxn) this.pollCache.delete(id);
    let p = this.pollCache.get(id);
    if (!p) {
      const y = this.polls.get(id);
      if (!y) return undefined;
      p = this.readPoll(y);
      this.pollCache.set(id, p);
    }
    return p;
  }

  /** Live polls, newest first. */
  listPolls(): PollData[] {
    if (this.inTxn) this.pollList = null;
    if (!this.pollList) {
      this.pollList = [...this.polls.keys()]
        .map((id) => this.getPoll(id)!)
        .filter((p) => p && p.deletedAt === null)
        .sort((a, b) => b.createdAt - a.createdAt);
    }
    return this.pollList;
  }

  trashedPolls(): PollData[] {
    return [...this.polls.keys()].map((id) => this.getPoll(id)!).filter((p) => p && p.deletedAt !== null);
  }

  createPoll(input: NewPoll, createdBy = this.actorId, createdAt = now()): string {
    const id = input.id ?? uid();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      this.polls.set(id, y);
      y.set('id', id);
      y.set('question', input.question);
      y.set('description', input.description ?? '');
      y.set('mode', input.mode ?? 'single');
      y.set('options', input.options.map((o) => ({ ...o, id: o.id ?? uid(8) })));
      y.set('anonymous', input.anonymous ?? false);
      y.set('status', 'open');
      y.set('closesAt', input.closesAt ?? null);
      y.set('decision', null);
      y.set('decisionNote', '');
      y.set('linkedRecordId', input.linkedRecordId ?? null);
      y.set('order', orderBetween(null, null));
      y.set('createdAt', createdAt);
      y.set('createdBy', createdBy);
      y.set('closedAt', null);
      y.set('deletedAt', null);
      y.set('votes', new Y.Map<PollVote>());
    });
    return id;
  }

  updatePoll(id: string, patch: Partial<Pick<PollData, 'question' | 'description' | 'mode' | 'options' | 'anonymous' | 'closesAt' | 'linkedRecordId' | 'decisionNote'>>) {
    const y = this.polls.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (v !== undefined) y.set(k, v);
    });
  }

  /** Toggle an option for the actor (single mode replaces the previous choice). */
  togglePollChoice(pollId: string, optionId: string, actorId = this.actorId) {
    const poll = this.getPoll(pollId);
    const votes = this.polls.get(pollId)?.get('votes') as Y.Map<PollVote> | undefined;
    if (!poll || !votes || !actorId || poll.status === 'closed') return;
    const current = poll.votes[actorId]?.choices ?? [];
    let choices: string[];
    if (poll.mode === 'single') choices = current.includes(optionId) ? [] : [optionId];
    else choices = current.includes(optionId) ? current.filter((c) => c !== optionId) : [...current, optionId];
    this.transact(() => {
      if (choices.length) votes.set(actorId, { choices, at: now() });
      else votes.delete(actorId);
    });
  }

  /** Score mode: rate one option 1..5 (0 clears). */
  setPollScore(pollId: string, optionId: string, score: number, actorId = this.actorId) {
    const poll = this.getPoll(pollId);
    const votes = this.polls.get(pollId)?.get('votes') as Y.Map<PollVote> | undefined;
    if (!poll || !votes || !actorId || poll.status === 'closed') return;
    const scores = { ...(poll.votes[actorId]?.scores ?? {}) };
    if (score > 0) scores[optionId] = Math.max(1, Math.min(5, Math.round(score)));
    else delete scores[optionId];
    this.transact(() => {
      if (Object.keys(scores).length) votes.set(actorId, { scores, at: now() });
      else votes.delete(actorId);
    });
  }

  /** Write a vote verbatim (templates / imports). */
  putPollVote(pollId: string, userId: string, vote: PollVote) {
    const votes = this.polls.get(pollId)?.get('votes') as Y.Map<PollVote> | undefined;
    if (votes) this.system(() => votes.set(userId, vote));
  }

  closePoll(id: string, decision: string | null, note = '') {
    const y = this.polls.get(id);
    if (!y) return;
    this.transact(() => {
      y.set('status', 'closed');
      y.set('decision', decision);
      y.set('decisionNote', note);
      y.set('closedAt', now());
    });
  }

  reopenPoll(id: string) {
    const y = this.polls.get(id);
    if (!y) return;
    this.transact(() => {
      y.set('status', 'open');
      y.set('decision', null);
      y.set('closedAt', null);
    });
  }

  trashPoll(id: string) {
    this.polls.get(id) && this.transact(() => this.polls.get(id)!.set('deletedAt', now()));
  }

  restorePoll(id: string) {
    this.polls.get(id) && this.transact(() => this.polls.get(id)!.set('deletedAt', null));
  }

  destroyPoll(id: string) {
    this.transact(() => this.polls.delete(id));
  }

  /* ============================================================================================
   * Boards
   * ========================================================================================== */

  private readBoard(y: YMap): BoardData {
    return {
      id: str(y.get('id')),
      name: str(y.get('name'), 'Untitled board'),
      description: str(y.get('description')),
      order: str(y.get('order'), 'a0'),
      createdAt: num(y.get('createdAt')),
      createdBy: strOrNull(y.get('createdBy')),
      deletedAt: numOrNull(y.get('deletedAt')),
    };
  }

  getBoard(id: string | null | undefined): BoardData | undefined {
    if (!id) return undefined;
    if (this.inTxn) this.boardCache.delete(id);
    let b = this.boardCache.get(id);
    if (!b) {
      const y = this.boards.get(id);
      if (!y) return undefined;
      b = this.readBoard(y);
      this.boardCache.set(id, b);
    }
    return b;
  }

  listBoards(): BoardData[] {
    if (this.inTxn) this.boardList = null;
    if (!this.boardList) {
      this.boardList = sortByOrder([...this.boards.keys()].map((id) => this.getBoard(id)!).filter((b) => b && b.deletedAt === null));
    }
    return this.boardList;
  }

  trashedBoards(): BoardData[] {
    return [...this.boards.keys()].map((id) => this.getBoard(id)!).filter((b) => b && b.deletedAt !== null);
  }

  private itemsY(boardId: string) {
    return this.boards.get(boardId)?.get('items') as Y.Map<YMap> | undefined;
  }

  listBoardItems(boardId: string): BoardItem[] {
    if (this.inTxn) this.boardItems.delete(boardId);
    let items = this.boardItems.get(boardId);
    if (!items) {
      items = [];
      this.itemsY(boardId)?.forEach((y) => {
        const votes = y.get('votes') as Y.Map<number> | undefined;
        items!.push({
          id: str(y.get('id')),
          type: itemType(y.get('type')),
          x: num(y.get('x')),
          y: num(y.get('y')),
          w: num(y.get('w'), 200),
          h: num(y.get('h'), 160),
          color: color(y.get('color'), 'yellow'),
          text: str(y.get('text')),
          z: num(y.get('z')),
          createdAt: num(y.get('createdAt')),
          createdBy: strOrNull(y.get('createdBy')),
          votes: voteSet(votes),
          promotedTo: strOrNull(y.get('promotedTo')),
          shape: shape(y.get('shape')),
          points: points(y.get('points')),
          strokeWidth: num(y.get('strokeWidth'), 3),
          filled: y.get('filled') === true,
          file: file(y.get('file')),
        });
      });
      items.sort((a, b) => a.z - b.z || (a.id < b.id ? -1 : 1));
      this.boardItems.set(boardId, items);
    }
    return items;
  }

  createBoard(input: { id?: string; name: string; description?: string }): string {
    const id = input.id ?? uid();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      this.boards.set(id, y);
      y.set('id', id);
      y.set('name', input.name);
      y.set('description', input.description ?? '');
      y.set('order', orderAfterLast(this.listBoards()));
      y.set('createdAt', now());
      y.set('createdBy', this.actorId);
      y.set('deletedAt', null);
      y.set('items', new Y.Map<YMap>());
    });
    return id;
  }

  updateBoard(id: string, patch: Partial<Pick<BoardData, 'name' | 'description' | 'order'>>) {
    const y = this.boards.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (v !== undefined) y.set(k, v);
    });
  }

  trashBoard(id: string) {
    this.boards.get(id) && this.transact(() => this.boards.get(id)!.set('deletedAt', now()));
  }

  restoreBoard(id: string) {
    this.boards.get(id) && this.transact(() => this.boards.get(id)!.set('deletedAt', null));
  }

  destroyBoard(id: string) {
    this.transact(() => this.boards.delete(id));
  }

  addBoardItems(boardId: string, items: NewBoardItem[]): string[] {
    const itemsY = this.itemsY(boardId);
    if (!itemsY) return [];
    let z = this.listBoardItems(boardId).reduce((m, it) => Math.max(m, it.z), 0);
    const ids: string[] = [];
    this.transact(() => {
      for (const it of items) {
        const id = it.id ?? uid();
        ids.push(id);
        const y = new Y.Map<unknown>();
        itemsY.set(id, y);
        y.set('id', id);
        y.set('type', it.type ?? 'note');
        y.set('x', Math.round(it.x));
        y.set('y', Math.round(it.y));
        y.set('w', it.w ?? (it.type === 'text' ? 280 : 200));
        y.set('h', it.h ?? (it.type === 'text' ? 60 : 160));
        y.set('color', it.color ?? 'yellow');
        y.set('text', it.text ?? '');
        y.set('z', ++z);
        y.set('createdAt', now());
        y.set('createdBy', it.createdBy !== undefined ? it.createdBy : this.actorId);
        y.set('promotedTo', null);
        // Only the keys a type actually uses are written, so a note stays as small as it was.
        if (it.type === 'shape') {
          y.set('shape', it.shape ?? 'rect');
          y.set('filled', it.filled ?? false);
        }
        if (it.points?.length) y.set('points', [...it.points]);
        if (it.strokeWidth !== undefined) y.set('strokeWidth', it.strokeWidth);
        if (it.file) y.set('file', it.file);
        const votes = new Y.Map<number>();
        y.set('votes', votes);
        for (const [k, v] of Object.entries(it.votes ?? {})) votes.set(k, v);
      }
    });
    return ids;
  }

  updateBoardItems(
    boardId: string,
    patches: { id: string; patch: Partial<Pick<BoardItem, 'x' | 'y' | 'w' | 'h' | 'color' | 'text' | 'type' | 'promotedTo' | 'shape' | 'points' | 'strokeWidth' | 'filled' | 'file'>> }[],
  ) {
    const itemsY = this.itemsY(boardId);
    if (!itemsY) return;
    this.transact(() => {
      for (const { id, patch } of patches) {
        const y = itemsY.get(id);
        if (!y) continue;
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) continue;
          if (k === 'points') y.set(k, [...(v as readonly number[])]);
          else y.set(k, typeof v === 'number' && (k === 'x' || k === 'y' || k === 'w' || k === 'h') ? Math.round(v) : v);
        }
      }
    });
  }

  removeBoardItems(boardId: string, ids: string[]) {
    const itemsY = this.itemsY(boardId);
    if (!itemsY) return;
    this.transact(() => ids.forEach((id) => itemsY.delete(id)));
  }

  bringBoardItemsToFront(boardId: string, ids: string[]) {
    const itemsY = this.itemsY(boardId);
    if (!itemsY) return;
    let z = this.listBoardItems(boardId).reduce((m, it) => Math.max(m, it.z), 0);
    this.transact(() => ids.forEach((id) => itemsY.get(id)?.set('z', ++z)));
  }

  /** How many items on a board the actor has voted for. */
  boardVotesUsed(boardId: string, actorId = this.actorId): number {
    if (!actorId) return 0;
    return this.listBoardItems(boardId).reduce((s, it) => s + (it.votes[actorId] ? 1 : 0), 0);
  }

  /** Whether the actor has voted for an item. */
  hasBoardVote(boardId: string, itemId: string, actorId = this.actorId): boolean {
    if (!actorId) return false;
    return !!this.listBoardItems(boardId).find((it) => it.id === itemId)?.votes[actorId];
  }

  /**
   * Cast or withdraw the actor's vote on an item. One vote per person per item and no budget, so
   * this is a toggle rather than a running total. Only notes and pictures can be voted on.
   */
  voteBoardItem(boardId: string, itemId: string, on: boolean, actorId = this.actorId): boolean {
    const item = this.itemsY(boardId)?.get(itemId);
    const votes = item?.get('votes') as Y.Map<number> | undefined;
    if (!item || !votes || !actorId || !isVotable(itemType(item.get('type')))) return false;
    if (on === votes.has(actorId)) return !this.readOnlyFlag;
    this.transact(() => {
      if (on) votes.set(actorId, 1);
      else votes.delete(actorId);
    });
    return !this.readOnlyFlag;
  }

  /** Flip the actor's vote on an item. Returns true when the vote is now cast. */
  toggleBoardVote(boardId: string, itemId: string, actorId = this.actorId): boolean {
    const next = !this.hasBoardVote(boardId, itemId, actorId);
    this.voteBoardItem(boardId, itemId, next, actorId);
    return next;
  }

  clearBoardVotes(boardId: string) {
    const itemsY = this.itemsY(boardId);
    if (!itemsY) return;
    this.transact(() => itemsY.forEach((y) => (y.get('votes') as Y.Map<number> | undefined)?.clear()));
  }

  /* ============================================================================================
   * Stats used by the overview & sidebar
   * ========================================================================================== */

  counts(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const c of this.listCollections()) out[c.id] = this.listRecords(c.id).length;
    return out;
  }
}

/** Create a ProjectDoc for a fresh Y.Doc (tests, imports, templates). */
export function createProjectDoc(meta: Pick<ProjectMeta, 'id' | 'name'> & Partial<ProjectMeta>, doc = new Y.Doc()): ProjectDoc {
  const p = new ProjectDoc(doc);
  p.initialize(meta);
  return p;
}
